// Pestaña "Correos" de la pantalla del asistente: filtros, resumen por estado, tabla paginada y detalle.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Filter, Search, RefreshCw, Paperclip } from 'lucide-react';
import { api } from '../../api/client';
import { useUIStore } from '../../stores/uiStore';
import { DataTable } from '../../components/shared/DataTable';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { Pagination } from '../../components/shared/Pagination';
import { usePagination } from '../../hooks/usePagination';
import { EmailDetailDrawer } from './EmailDetailDrawer';
import {
  loadMeta,
  typeLabel,
  statusLabel,
  statusTone,
  fmtDateTime,
  folderLabel,
  type AssistantMeta,
  type EmailRow,
} from './shared';

interface Summary {
  by_type: Array<{ type: string | null; count: number }>;
  by_status: Array<{ status: string; count: number }>;
  by_seller: Array<{ seller_id: number; count: number }>;
  inbox_unread: number;
}

type Row = EmailRow & Record<string, unknown>;

const MAX_PER_PAGE = 200;

const inputCls =
  'px-2 py-1 text-xs bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded text-[var(--color-text)]';

export default function EmailsTab({
  onOpenCase,
  onChanged,
}: {
  onOpenCase?: (caseId: number) => void;
  /** Se avisa cuando algo cambió (p. ej. un reintento): refresca el contador de casos. */
  onChanged?: () => void;
} = {}) {
  const sellerId = useUIStore((s) => s.sellerId);
  const sellers = useUIStore((s) => s.sellers);
  const pag = usePagination('assistant-emails');

  const [meta, setMeta] = useState<AssistantMeta | null>(null);
  const [metaFailed, setMetaFailed] = useState(false);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [folder, setFolder] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<number | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // Solo vale la respuesta del último pedido (si llegan desordenadas, se descartan las viejas).
  const reqSeq = useRef(0);

  // Las etiquetas de tipos y estados se esperan ANTES de dibujar la tabla (así nunca se ve la clave cruda).
  useEffect(() => {
    let alive = true;
    void loadMeta().then((m) => {
      if (!alive) return;
      if (m) setMeta(m);
      else setMetaFailed(true);
    });
    return () => {
      alive = false;
    };
  }, []);

  const sellerName = useMemo(() => {
    const map = new Map<number, string>();
    for (const s of sellers) map.set(s.id, s.nickname);
    return (id: number) => map.get(id) ?? String(id);
  }, [sellers]);

  const loadSummary = useCallback(async () => {
    const res = await api.get<Summary>(`/assistant/summary?seller_id=${sellerId}`);
    if (res.success && res.data) setSummary(res.data);
  }, [sellerId]);

  // El servidor devuelve hasta 200 por página: si quedó guardado 300, se baja a 200.
  useEffect(() => {
    if (pag.perPage > MAX_PER_PAGE) pag.setPerPage(MAX_PER_PAGE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pag.perPage]);

  const loadList = useCallback(async () => {
    if (pag.perPage > MAX_PER_PAGE) return;
    const seq = ++reqSeq.current;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        seller_id: String(sellerId),
        page: String(pag.page),
        per_page: String(pag.perPage),
      });
      if (type) params.set('type', type);
      if (status) params.set('status', status);
      if (folder) params.set('folder', folder);
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      if (q) params.set('q', q);
      const res = await api.get<EmailRow[]>(`/assistant/emails?${params}`);
      if (seq !== reqSeq.current) return;
      if (res.success && res.data) {
        setRows(res.data as Row[]);
        pag.setTotal((res as any).meta?.total || 0);
      } else {
        setRows([]);
        pag.setTotal(0);
        setError(res.error || 'No se pudieron cargar los correos.');
      }
    } catch {
      if (seq === reqSeq.current) setError('No se pudieron cargar los correos.');
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerId, pag.page, pag.perPage, type, status, folder, from, to, q]);

  useEffect(() => {
    void loadSummary();
  }, [loadSummary, reloadKey]);

  useEffect(() => {
    void loadList();
  }, [loadList, reloadKey]);

  // Cambió un filtro o el vendedor: volver a la primera página.
  useEffect(() => {
    pag.resetPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerId, type, status, folder, from, to, q]);

  const countOf = (keys: string[]) =>
    (summary?.by_status ?? [])
      .filter((s) => keys.includes(s.status))
      .reduce((acc, s) => acc + s.count, 0);

  const chips = [
    { label: 'Clasificados', value: countOf(['classified', 'done']), cls: 'text-[var(--color-success)]' },
    { label: 'Sin clasificar', value: countOf(['unclassified']), cls: 'text-[var(--color-warning)]' },
    { label: 'Fallidos', value: countOf(['failed']), cls: 'text-[var(--color-error)]' },
  ];
  const totalAll = (summary?.by_status ?? []).reduce((acc, s) => acc + s.count, 0);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setQ(qInput.trim());
  };

  const clearFilters = () => {
    setType('');
    setStatus('');
    setFolder('');
    setFrom('');
    setTo('');
    setQInput('');
    setQ('');
  };

  const columns = [
    {
      key: 'received_at',
      label: 'Fecha',
      render: (r: Row) => (
        <span className="text-xs text-[var(--color-text-secondary)] whitespace-nowrap">
          {fmtDateTime(r.received_at)}
        </span>
      ),
    },
    {
      key: 'seller_id',
      label: 'Empresa',
      render: (r: Row) => <span className="text-xs whitespace-nowrap">{sellerName(r.seller_id)}</span>,
    },
    {
      key: 'folder',
      label: 'Carpeta',
      render: (r: Row) => <span className="text-xs">{folderLabel(r.folder)}</span>,
    },
    {
      key: 'from_addr',
      label: 'Remitente',
      render: (r: Row) => (
        <span className="text-xs block max-w-[220px] truncate" title={r.from_addr ?? ''}>
          {r.from_addr || '—'}
        </span>
      ),
    },
    {
      key: 'subject',
      label: 'Asunto',
      render: (r: Row) => (
        <span className="block max-w-[360px] truncate" title={r.subject ?? ''}>
          {r.subject || '(sin asunto)'}
        </span>
      ),
    },
    {
      key: 'type',
      label: 'Tipo',
      render: (r: Row) => <span className="text-xs whitespace-nowrap">{typeLabel(r.type)}</span>,
    },
    {
      key: 'status',
      label: 'Estado',
      render: (r: Row) => <StatusBadge status={statusTone(r.status)} label={statusLabel(r.status)} size="sm" />,
    },
    {
      key: 'attachments_count',
      label: 'Adjuntos',
      render: (r: Row) =>
        r.attachments_count > 0 ? (
          <span className="inline-flex items-center gap-1 text-xs">
            <Paperclip size={12} />
            {r.attachments_count}
          </span>
        ) : (
          <span className="text-xs text-[var(--color-text-muted)]">—</span>
        ),
    },
  ];

  if (!meta) {
    return (
      <div className="text-center py-8 text-[var(--color-text-secondary)]">
        {metaFailed ? 'No se pudieron cargar las listas del asistente. Recargá la página.' : 'Cargando…'}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Resumen por estado (del vendedor elegido) */}
      <div className="flex flex-wrap gap-2">
        <div className="px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] text-sm">
          <span className="text-[var(--color-text-secondary)]">Total: </span>
          <span className="font-semibold text-[var(--color-text)]">{totalAll.toLocaleString()}</span>
        </div>
        {chips.map((c) => (
          <div
            key={c.label}
            className="px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] text-sm"
          >
            <span className="text-[var(--color-text-secondary)]">{c.label}: </span>
            <span className={`font-semibold ${c.cls}`}>{c.value.toLocaleString()}</span>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <form
        onSubmit={submitSearch}
        className="flex flex-wrap items-center gap-2 bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg p-3"
      >
        <Filter className="w-4 h-4 text-[var(--color-text-secondary)]" />
        <select value={type} onChange={(e) => setType(e.target.value)} className={inputCls}>
          <option value="">Todos los tipos</option>
          {meta.types.map((t) => (
            <option key={t.key} value={t.key}>
              {t.label}
            </option>
          ))}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls}>
          <option value="">Todos los estados</option>
          {meta.statuses.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </select>
        <select value={folder} onChange={(e) => setFolder(e.target.value)} className={inputCls}>
          <option value="">Todas las carpetas</option>
          {/* La carpeta vacía no se ofrece: el servidor no filtra por carpeta vacía. */}
          {meta.folders
            .filter((f) => f !== '')
            .map((f) => (
              <option key={f} value={f}>
                {folderLabel(f)}
              </option>
            ))}
        </select>
        <label className="flex items-center gap-1 text-xs text-[var(--color-text-secondary)]">
          Desde
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inputCls} />
        </label>
        <label className="flex items-center gap-1 text-xs text-[var(--color-text-secondary)]">
          Hasta
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inputCls} />
        </label>
        <input
          type="text"
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
          placeholder="Buscar en remitente, asunto o texto"
          className={`${inputCls} min-w-[220px] flex-1`}
        />
        <button
          type="submit"
          className="inline-flex items-center gap-1 px-3 py-1 text-xs rounded bg-[var(--color-primary)] text-white hover:opacity-90"
        >
          <Search size={12} /> Buscar
        </button>
        <button
          type="button"
          onClick={clearFilters}
          className="px-3 py-1 text-xs rounded border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text)]"
        >
          Limpiar
        </button>
        <button
          type="button"
          onClick={() => setReloadKey((k) => k + 1)}
          title="Actualizar"
          className="p-1 rounded border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text)]"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </form>

      {error && (
        <div className="rounded-lg p-3 text-sm bg-[var(--color-error-bg,rgba(239,68,68,0.1))] text-[var(--color-error)]">
          {error}
        </div>
      )}

      <DataTable<Row>
        columns={columns}
        data={rows}
        loading={loading}
        emptyMessage="No hay correos con estos filtros."
        onRowClick={(r) => setOpenId(r.id)}
      />

      <Pagination
        page={pag.page}
        perPage={pag.perPage}
        total={pag.total}
        totalPages={pag.totalPages}
        onPageChange={pag.setPage}
        onPerPageChange={pag.setPerPage}
      />

      {openId !== null && (
        <EmailDetailDrawer
          emailId={openId}
          sellerName={sellerName}
          onClose={() => setOpenId(null)}
          onChanged={() => {
            setReloadKey((k) => k + 1);
            onChanged?.();
          }}
          onOpenCase={
            onOpenCase
              ? (id) => {
                  setOpenId(null);
                  onOpenCase(id);
                }
              : undefined
          }
        />
      )}
    </div>
  );
}
